import * as tf from '@tensorflow/tfjs';
import {
  RGBtoHSV,
  hexToRgb,
  calculateColorSimilarity,
} from '../utils/colorUtils';
import colorPalettes from '../assets/color-palettes.json';
import { saveSkinAnalysis } from '../api/colours';
import {
  Season,
  SEASON_SUBTYPES,
  SUBTYPE_BY_LABEL,
} from '../constants/seasonalPalettes';
import type { SkinAnalysisRegion } from '../utils/faceRoi';
import {
  type SeasonUndertone,
  legacyUndertoneForApi,
  seasonFromSkin,
} from '../utils/seasonUndertone';
import { extractFacialColorFeatures } from '../utils/facialColorFeatures';
import {
  classifyHierarchical,
  type HierarchicalSeasonPrediction,
} from '../utils/seasonalHierarchicalClassifier';
import { EXPO_PUBLIC_HIERARCHICAL_SEASONAL } from '@env';
export type { SkinAnalysisRegion } from '../utils/faceRoi';

// ─── Feature flags ─────────────────────────────────────────────────────────────
/**
 * When true the service prefers the backend two-stage seasonal classifier.
 * Falls back to heuristic if the backend call fails or confidence is too low.
 */
export const SEASONAL_DETECTION_ENABLED =
  process.env['EXPO_PUBLIC_SEASONAL_DETECTION'] === 'true';

/**
 * When true the frontend hierarchical 12-type classifier runs instead of
 * (and takes precedence over) both the heuristic and the backend model.
 * The backend call is skipped when this flag is on to avoid conflicting results.
 */
export const HIERARCHICAL_SEASONAL_ENABLED = EXPO_PUBLIC_HIERARCHICAL_SEASONAL === 'true';

/** Minimum model confidence to accept a backend prediction (0–1). */
const SEASONAL_CONFIDENCE_THRESHOLD = 0.55;

// ─── Season / subtype prediction types ────────────────────────────────────────
export interface SeasonPrediction {
  season: Season;
  subtype: string;           // stable ID, e.g. "spring.light"
  subtypeLabel: string;      // display label, e.g. "Light Spring"
  seasonConfidence: number;  // 0–1
  subtypeConfidence: number; // 0–1
  source: 'model' | 'heuristic' | 'hierarchical';
}

// Types of skin undertones
export type UndertoneType = SeasonUndertone;

// Color analysis result interface
export interface ColorAnalysisResult {
  score: number;
  quality: 'Best' | 'Great' | 'Okay' | 'Tricky' | 'Avoid';
  feedback: string;
  tip: string;
  confidence: number;
  undertoneMatch: number;
  contrastScore: number;
  harmonyScore: number;
}

// Skin tone data interface
export interface SkinToneData {
  undertone: UndertoneType;
  skinToneCategory: string;
  confidence: number;
  dominantColors: Array<{ r: number; g: number; b: number; hex: string }>;
}

// Makeup recommendation interface
export interface MakeupRecommendations {
  foundation: string;
  lipstick: string;
  concealer: string;
  blush: string;
  eyeshadow: string;
  eyeliner: string;
  hairColor: string;
  contactLensColor: string;
  nailColor: string;
  highlighter?: string;
}

// Color palette based on skin undertone
export interface ColorPalette {
  undertone: UndertoneType;
  colors: string[];
  completePalette?: string[];
  name: string;
  description: string;
  avoidColors?: string[];
  makeupRecommendations?: MakeupRecommendations;
}

// ─── Module-level palette helpers ─────────────────────────────────────────────

const buildUndertonePaletteMap = (): Record<UndertoneType, ColorPalette[]> => {
  const source = colorPalettes as Record<string, unknown>;
  const spring = Array.isArray(source.spring) ? (source.spring as ColorPalette[]) : [];
  const summer = Array.isArray(source.summer) ? (source.summer as ColorPalette[]) : [];
  const autumn = Array.isArray(source.autumn) ? (source.autumn as ColorPalette[]) : [];
  const winter = Array.isArray(source.winter) ? (source.winter as ColorPalette[]) : [];
  return {
    spring: [...spring],
    summer: [...summer],
    autumn: [...autumn],
    winter: [...winter],
  };
};

const undertonePalettes = buildUndertonePaletteMap();

/** Resolve a palette deterministically from a SeasonPrediction. */
function paletteFromPrediction(prediction: SeasonPrediction): ColorPalette | null {
  const source = colorPalettes as Record<string, unknown>;
  const seasonArr = Array.isArray(source[prediction.season])
    ? (source[prediction.season] as ColorPalette[])
    : [];
  const subtypeEntry = SUBTYPE_BY_LABEL[prediction.subtypeLabel.toLowerCase()];
  if (subtypeEntry && seasonArr[subtypeEntry.paletteIndex]) {
    return { ...seasonArr[subtypeEntry.paletteIndex] } as ColorPalette;
  }
  const byName = seasonArr.find(
    (p) => (p as ColorPalette).name?.toLowerCase() === prediction.subtypeLabel.toLowerCase()
  );
  if (byName) return { ...byName } as ColorPalette;
  return seasonArr.length > 0 ? ({ ...seasonArr[0] } as ColorPalette) : null;
}

/**
 * Build a heuristic SeasonPrediction from an undertone string.
 *
 * `skinValue` (0–1) is the median HSV brightness of the detected skin pixels.
 * `skinSat`   (0–1) is the median HSV saturation.
 * When these are supplied the function picks the most appropriate subtype
 * instead of always defaulting to index 0.
 *
 * Subtype index mapping (matches seasonalPalettes.ts):
 *   spring  0=Light Spring  1=Bright Spring  2=Warm Spring
 *   summer  0=Light Summer  1=Muted Summer   2=Cool Summer
 *   autumn  0=Dark Autumn   1=Muted Autumn   2=Warm Autumn
 *   winter  0=Dark Winter   1=Bright Winter  2=Cool Winter
 */
function heuristicPrediction(
  undertone: UndertoneType,
  skinValue = 0.55,
  skinSat = 0.30
): SeasonPrediction {
  const season = undertone as Season;
  const subtypes = SEASON_SUBTYPES.filter((s) => s.season === season);

  let index = 0;

  switch (season) {
    case 'spring':
      // Light Spring: bright & light (high V, moderate S)
      // Bright Spring: vivid (high S)
      // Warm Spring: deeper warm (lower V)
      if (skinSat >= 0.42) index = 1;        // high saturation → Bright Spring
      else if (skinValue >= 0.60) index = 0; // light & clear → Light Spring
      else index = 2;                        // warmer/deeper → Warm Spring
      break;

    case 'summer':
      // Light Summer: high V, lower S (soft)
      // Muted Summer: medium V, low S (dusty)
      // Cool Summer: lower V, moderate S (deeper cool)
      if (skinValue >= 0.60) index = 0;      // lighter → Light Summer
      else if (skinSat < 0.22) index = 1;    // muted/dusty → Muted Summer
      else index = 2;                        // deeper cool → Cool Summer
      break;

    case 'autumn':
      // Dark Autumn: deep/dark (low V)
      // Muted Autumn: medium V, low S
      // Warm Autumn: medium V, higher S (rich warm)
      if (skinValue < 0.42) index = 0;       // deep/dark → Dark Autumn
      else if (skinSat >= 0.32) index = 2;   // rich warm → Warm Autumn
      else index = 1;                        // softer/muted → Muted Autumn
      break;

    case 'winter':
      // Dark Winter: very deep (low V)
      // Bright Winter: high contrast, medium V
      // Cool Winter: clear cool, higher V
      if (skinValue < 0.42) index = 0;       // deep/dark → Dark Winter
      else if (skinSat >= 0.38) index = 1;   // high contrast → Bright Winter
      else index = 2;                        // clear cool → Cool Winter
      break;
  }

  const picked = subtypes[index] ?? subtypes[0];
  return {
    season,
    subtype:           picked?.id ?? `${season}.default`,
    subtypeLabel:      picked?.label ?? season,
    seasonConfidence:  0.5,
    subtypeConfidence: 0.45,
    source:            'heuristic',
  };
}

class SkinUndertoneService {
  private isInitialized: boolean = false;
  private skinToneData: SkinToneData | null = null;
  /** Last hierarchical prediction; set during analyzeSkinUndertoneAdvanced when flag is on. */
  private _hierarchicalPrediction: HierarchicalSeasonPrediction | null = null;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Initialize TensorFlow if available
      if (typeof tf !== 'undefined') {
        await tf.ready();
      }

      // Run test analysis to verify logic
      this.testUndertoneAnalysis();

      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing SkinUndertoneService:', error);
      this.isInitialized = true; // Mark as initialized even if TensorFlow fails
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Analyze an image to extract skin tone information
   */
  async analyzeImage(imageData: ImageData): Promise<SkinToneData> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      const pixels = imageData.data;
      const width = imageData.width;
      const height = imageData.height;

      // Sample pixels from the image (every 10th pixel for performance)
      const skinSamples: Array<{ r: number; g: number; b: number }> = [];
      const allSamples: Array<{ r: number; g: number; b: number }> = [];

      for (let y = 0; y < height; y += 10) {
        for (let x = 0; x < width; x += 10) {
          const index = (y * width + x) * 4;
          const r = pixels[index];
          const g = pixels[index + 1];
          const b = pixels[index + 2];
          const a = pixels[index + 3];

          // Skip transparent pixels
          if (a < 128) continue;

          allSamples.push({ r, g, b });

          // Detect skin pixels using HSV analysis
          const hsv = RGBtoHSV(r, g, b);
          if (this.isSkinPixel(hsv)) {
            skinSamples.push({ r, g, b });
          }
        }
      }

      if (skinSamples.length === 0) {
        // If no skin detected, analyze the overall image
        return this.analyzeOverallImage(allSamples);
      }

      // Use the sophisticated undertone analysis instead of simple single-pixel analysis
      const sophisticatedUndertone = await this.analyzeSkinUndertone(imageData);

      // Analyze skin tone from detected skin pixels for category and confidence
      const skinAnalysis = this.analyzeSkinTone(
        skinSamples[0].r,
        skinSamples[0].g,
        skinSamples[0].b
      );

      // Get dominant skin colors
      const dominantColors = this.getDominantColors(skinSamples, 5);

      this.skinToneData = {
        undertone: sophisticatedUndertone, // Use the sophisticated analysis result
        skinToneCategory: skinAnalysis.skinToneCategory,
        confidence: skinAnalysis.confidence,
        dominantColors,
      };

      return this.skinToneData;
    } catch (error) {
      console.error('Error analyzing image:', error);
      // Return default neutral analysis
      return {
        undertone: 'autumn',
        skinToneCategory: 'Unknown',
        confidence: 0,
        dominantColors: [],
      };
    }
  }

  /**
   * Get the current skin tone data
   */
  getSkinToneData(): SkinToneData | null {
    return this.skinToneData;
  }

  /**
   * Reset the service state
   */
  reset(): void {
    this.skinToneData = null;
    this._hierarchicalPrediction = null;
  }

  /**
   * Detect if a pixel is likely skin tone
   */
  private isSkinPixel(hsv: { h: number; s: number; v: number }): boolean {
    const h = hsv.h * 360; // Convert to degrees
    const s = hsv.s;
    const v = hsv.v;

    // Skin tone ranges in HSV
    return (
      s >= 0.05 &&
      s <= 0.9 && // Saturation range
      v >= 0.15 &&
      v <= 0.98 && // Value range
      ((h >= 0 && h <= 60) || (h >= 330 && h <= 360)) // Hue range for skin tones
    );
  }

  /**
   * Get dominant colors from a set of samples
   */
  private getDominantColors(
    samples: Array<{ r: number; g: number; b: number }>,
    count: number
  ) {
    // Simple approach: group similar colors and count occurrences
    const colorGroups: Array<{
      r: number;
      g: number;
      b: number;
      hex: string;
      count: number;
    }> = [];

    samples.forEach((sample) => {
      const hex = `#${Math.round(sample.r).toString(16).padStart(2, '0')}${Math.round(
        sample.g
      )
        .toString(16)
        .padStart(
          2,
          '0'
        )}${Math.round(sample.b).toString(16).padStart(2, '0')}`;

      // Find if this color is similar to existing groups
      let found = false;
      for (const group of colorGroups) {
        if (calculateColorSimilarity(hex, group.hex) > 0.8) {
          group.count++;
          found = true;
          break;
        }
      }

      if (!found) {
        colorGroups.push({ ...sample, hex, count: 1 });
      }
    });

    // Sort by count and return top colors
    return colorGroups
      .sort((a, b) => b.count - a.count)
      .slice(0, count)
      .map(({ r, g, b, hex }) => ({ r, g, b, hex }));
  }

  /**
   * Analyze overall image when no skin is detected
   */
  private analyzeOverallImage(
    samples: Array<{ r: number; g: number; b: number }>
  ): SkinToneData {
    // Default analysis for when no skin is detected
    return {
      undertone: 'autumn',
      skinToneCategory: 'Unknown',
      confidence: 0,
      dominantColors: samples.slice(0, 5).map((sample) => ({
        ...sample,
        hex: `#${Math.round(sample.r).toString(16).padStart(2, '0')}${Math.round(
          sample.g
        )
          .toString(16)
          .padStart(
            2,
            '0'
          )}${Math.round(sample.b).toString(16).padStart(2, '0')}`,
      })),
    };
  }

  /**
   * Analyze skin tone from RGB values
   */
  private analyzeSkinTone(
    r: number,
    g: number,
    b: number
  ): { undertone: UndertoneType; skinToneCategory: string; confidence: number } {
    const hsv = RGBtoHSV(r, g, b);
    const undertone = seasonFromSkin(hsv.h, hsv.s, hsv.v);
    const confidence = hsv.s < 0.25 ? 0.8 : 0.7;

    return {
      undertone,
      skinToneCategory: undertone.charAt(0).toUpperCase() + undertone.slice(1),
      confidence,
    };
  }

  /**
   * Get default analysis when no skin tone data is available
   */
  private getDefaultAnalysis(_colorHex: string): ColorAnalysisResult {
    return {
      score: 0.5,
      quality: 'Okay',
      feedback: 'No skin tone data available for analysis',
      tip: 'Please take a photo of your face to get personalized color recommendations',
      confidence: 0,
      undertoneMatch: 0.5,
      contrastScore: 0.5,
      harmonyScore: 0.5,
    };
  }

  /**
   * Calculate undertone match score using colorScoring data
   */
  private calculateUndertoneMatch(colorHex: string): number {
    if (!this.skinToneData) return 0.5;

    const colorRgb = hexToRgb(colorHex);
    if (!colorRgb) return 0.5;

    const undertone = this.skinToneData.undertone;
    const scoringKey = legacyUndertoneForApi(undertone);

    // Get color scoring data for the detected undertone
    const colorScoringData = (
      colorPalettes as {
        colorScoring?: Record<
          string,
          {
            best?: string[];
            great?: string[];
            okay?: string[];
            tricky?: string[];
            avoid?: string[];
          }
        >;
      }
    ).colorScoring?.[scoringKey];

    if (colorScoringData) {
      // Check which category the color falls into
      if (colorScoringData.best.includes(colorHex.toUpperCase())) {
        return 0.95; // Best match
      } else if (colorScoringData.great.includes(colorHex.toUpperCase())) {
        return 0.85; // Great match
      } else if (colorScoringData.okay.includes(colorHex.toUpperCase())) {
        return 0.7; // Okay match
      } else if (colorScoringData.tricky.includes(colorHex.toUpperCase())) {
        return 0.45; // Tricky match
      } else if (colorScoringData.avoid.includes(colorHex.toUpperCase())) {
        return 0.2; // Avoid match
      }
    }

    const hsv = RGBtoHSV(colorRgb.r, colorRgb.g, colorRgb.b);
    const h = hsv.h;

    if (scoringKey === 'warm') {
      return h >= 15 && h <= 45 ? 0.9 : h >= 0 && h <= 60 ? 0.7 : 0.3;
    } else if (scoringKey === 'cool') {
      return (h >= 0 && h < 15) || (h > 45 && h <= 60) || (h >= 330 && h <= 360)
        ? 0.9
        : 0.3;
    }
    return 0.6;
  }

  /**
   * Calculate contrast score
   */
  private calculateContrastScore(colorHex: string): number {
    if (!this.skinToneData || this.skinToneData.dominantColors.length === 0)
      return 0.5;

    const colorRgb = hexToRgb(colorHex);
    if (!colorRgb) return 0.5;

    // Calculate contrast against dominant skin color
    const skinColor = this.skinToneData.dominantColors[0];
    const contrast =
      Math.abs(colorRgb.r - skinColor.r) +
      Math.abs(colorRgb.g - skinColor.g) +
      Math.abs(colorRgb.b - skinColor.b);
    const maxContrast = 255 * 3;

    return Math.min(contrast / maxContrast, 1);
  }

  /**
   * Calculate harmony score
   */
  private calculateHarmonyScore(colorHex: string): number {
    if (!this.skinToneData) return 0.5;

    const undertoneMatch = this.calculateUndertoneMatch(colorHex);
    const contrastScore = this.calculateContrastScore(colorHex);

    // Balance between undertone match and contrast
    return undertoneMatch * 0.6 + contrastScore * 0.4;
  }

  /**
   * Calculate final score
   */
  private calculateFinalScore(
    undertoneMatch: number,
    contrastScore: number,
    harmonyScore: number
  ): number {
    return undertoneMatch * 0.4 + contrastScore * 0.3 + harmonyScore * 0.3;
  }

  /**
   * Get quality from score
   */
  private getQualityFromScore(
    score: number
  ): 'Best' | 'Great' | 'Okay' | 'Tricky' | 'Avoid' {
    if (score >= 0.8) return 'Best';
    if (score >= 0.7) return 'Great';
    if (score >= 0.6) return 'Okay';
    if (score >= 0.5) return 'Tricky';
    return 'Avoid';
  }

  /**
   * Generate feedback based on quality and color scoring data
   */
  private generateFeedback(
    quality: string,
    colorHex: string,
    _undertoneMatch: number
  ): string {
    const undertone = this.skinToneData?.undertone || 'autumn';
    const scoringKey =
      this.skinToneData?.undertone ? legacyUndertoneForApi(this.skinToneData.undertone) : 'neutral';

    // Get color scoring data for more specific feedback
    const colorScoringData = (
      colorPalettes as {
        colorScoring?: Record<
          string,
          {
            best?: string[];
            great?: string[];
            okay?: string[];
            tricky?: string[];
            avoid?: string[];
          }
        >;
      }
    ).colorScoring?.[scoringKey];

    switch (quality) {
      case 'Best':
        if (colorScoringData?.best.includes(colorHex.toUpperCase())) {
          return `This color is perfect for your ${undertone} undertone! It's in your "Best" category and will make you look radiant.`;
        }
        return `This color is perfect for your ${undertone} undertone!`;

      case 'Great':
        if (colorScoringData?.great.includes(colorHex.toUpperCase())) {
          return `This color works very well with your ${undertone} undertone. It's in your "Great" category and will enhance your natural beauty.`;
        }
        return `This color works very well with your ${undertone} undertone.`;

      case 'Okay':
        if (colorScoringData?.okay.includes(colorHex.toUpperCase())) {
          return `This color is acceptable for your ${undertone} undertone. It's in your "Okay" category and can work with the right styling.`;
        }
        return `This color is acceptable but not ideal for your undertone.`;

      case 'Tricky':
        if (colorScoringData?.tricky.includes(colorHex.toUpperCase())) {
          return `This color is challenging for your ${undertone} undertone. It's in your "Tricky" category - consider wearing it with confidence and complementary accessories.`;
        }
        return `This color might be challenging to wear with your undertone.`;

      case 'Avoid':
        if (colorScoringData?.avoid.includes(colorHex.toUpperCase())) {
          return `This color doesn't complement your ${undertone} undertone well. It's in your "Avoid" category - try similar colors with different undertones instead.`;
        }
        return `This color doesn't complement your undertone well.`;

      default:
        return 'Color analysis complete.';
    }
  }

  /**
   * Generate tip based on quality, undertone, and color scoring data
   */
  private generateTip(
    quality: string,
    colorHex: string,
    undertone: string
  ): string {
    // Get color scoring data for more specific tips
    const colorScoringData = (
      colorPalettes as {
        colorScoring?: Record<
          string,
          {
            best?: string[];
            great?: string[];
            okay?: string[];
            tricky?: string[];
            avoid?: string[];
          }
        >;
      }
    ).colorScoring?.[legacyUndertoneForApi(undertone as UndertoneType)];

    switch (quality) {
      case 'Best':
        if (colorScoringData?.best.includes(colorHex.toUpperCase())) {
          return `Perfect choice! This color is in your "Best" category and will make you look naturally radiant. Consider this for important occasions and photos.`;
        }
        return `Perfect choice! This color enhances your natural beauty.`;

      case 'Great':
        if (colorScoringData?.great.includes(colorHex.toUpperCase())) {
          return `Great pick! This color is in your "Great" category and will enhance your natural beauty. Perfect for everyday wear and professional settings.`;
        }
        return `Great pick! Consider this for important occasions.`;

      case 'Okay':
        if (colorScoringData?.okay.includes(colorHex.toUpperCase())) {
          return `This color is in your "Okay" category. Try pairing it with accessories in your best colors, or wear it as an accent piece rather than the main color.`;
        }
        return `Try pairing with accessories in your best colors.`;

      case 'Tricky':
        if (colorScoringData?.tricky.includes(colorHex.toUpperCase())) {
          return `This color is in your "Tricky" category. Wear it with confidence, but consider adjusting your makeup or pairing it with colors from your "Best" or "Great" categories.`;
        }
        return `Wear with confidence, but consider makeup adjustments.`;

      case 'Avoid':
        if (colorScoringData?.avoid.includes(colorHex.toUpperCase())) {
          return `This color is in your "Avoid" category. Instead, try similar colors with different undertones, or explore the colors in your "Best" and "Great" categories for better alternatives.`;
        }
        return `Try similar colors with different undertones.`;

      default:
        return 'Experiment with different shades to find your best colors.';
    }
  }

  /**
   * Analyze skin undertone using computer vision and machine learning
   */
  async analyzeSkinUndertone(imageData: ImageData): Promise<UndertoneType> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      // Extract pixel data from ImageData
      const pixels = imageData.data;
      const width = imageData.width;
      const height = imageData.height;

      // Collect skin pixel HSV values
      const skinPixels: number[][] = [];

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const index = (y * width + x) * 4;
          const r = pixels[index];
          const g = pixels[index + 1];
          const b = pixels[index + 2];
          const a = pixels[index + 3];

          // Skip transparent pixels
          if (a < 128) continue;

          // Convert RGB to HSV
          const hsv = RGBtoHSV(r, g, b);

          // Skin detection range in HSV - more inclusive
          const h = hsv.h;
          const s = hsv.s;
          const v = hsv.v;

          // Improved skin detection criteria - more inclusive
          if (
            s >= 0.05 && // Lower minimum saturation
            s <= 0.9 && // Higher maximum saturation
            v >= 0.15 && // Lower minimum value
            v <= 0.98 && // Higher maximum value
            ((h >= 0 && h <= 60) || (h >= 330 && h <= 360)) // Wider hue range for skin tones
          ) {
            skinPixels.push([h, s, v]);
          }
        }
      }

      if (skinPixels.length === 0) {
        return 'autumn';
      }

      // Calculate median H, S, V
      const median = (arr: number[]) => {
        const sorted = [...arr].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 === 0
          ? (sorted[mid - 1] + sorted[mid]) / 2
          : sorted[mid];
      };

      const hues = skinPixels.map((p) => p[0]);
      const sats = skinPixels.map((p) => p[1]);
      const vals = skinPixels.map((p) => p[2]);

      return seasonFromSkin(median(hues), median(sats), median(vals));
    } catch (err) {
      console.error('Error analyzing skin undertone:', err);
      return 'autumn';
    }
  }

  /**
   * Alternative method using TensorFlow for more advanced analysis
   */
  async analyzeSkinUndertoneWithTensorFlow(
    imageData: ImageData
  ): Promise<UndertoneType> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      // Convert ImageData to TensorFlow tensor
      const tensor = tf.browser.fromPixels(imageData, 3); // RGB only

      // Calculate mean color values
      const meanColor = tf.mean(tensor, [0, 1]); // Mean across height and width
      const meanValues = await meanColor.array();

      // Clean up tensors
      tensor.dispose();
      meanColor.dispose();

      // Handle the array properly - TensorFlow returns a nested array
      const meanArray = Array.isArray(meanValues) ? meanValues : [meanValues];
      const [r, g, b] = meanArray as number[];

      const hsv = RGBtoHSV(r, g, b);
      return seasonFromSkin(hsv.h, hsv.s, hsv.v);
    } catch (error) {
      console.error('Error in TensorFlow skin undertone analysis:', error);
      return 'autumn';
    }
  }

  /**
   * Request two-stage seasonal classification from the backend.
   * Returns null when the feature flag is off, the request fails, or
   * model confidence falls below the acceptance threshold.
   */
  async classifySeasonBackend(
    dominantColors: Array<{ hex: string }>,
    undertoneLegacy: 'warm' | 'cool' | 'neutral'
  ): Promise<SeasonPrediction | null> {
    if (!SEASONAL_DETECTION_ENABLED) return null;
    try {
      const { getSeasonalClassification } = await import('../api/colours');
      const result = await getSeasonalClassification({
        dominantColors,
        undertone: undertoneLegacy,
      });
      if (
        result.seasonConfidence < SEASONAL_CONFIDENCE_THRESHOLD ||
        result.subtypeConfidence < SEASONAL_CONFIDENCE_THRESHOLD
      ) {
        return null;
      }
      return { ...result, source: 'model' as const };
    } catch {
      return null;
    }
  }

  /**
   * Resolve the best palette for the given undertone, optionally using a
   * seasonal prediction.  If a prediction is supplied (from backend model or
   * heuristic) the palette is looked up deterministically by subtype label;
   * otherwise the first palette in the undertone bucket is used.
   */
  getColorPalette(
    undertone: UndertoneType,
    prediction?: SeasonPrediction
  ): ColorPalette {
    // Deterministic lookup via seasonal prediction
    if (prediction) {
      const resolved = paletteFromPrediction(prediction);
      if (resolved) return { ...resolved, undertone };
    }

    // Deterministic first-palette fallback from the undertone bucket
    const bucket = undertonePalettes[undertone];
    if (bucket && bucket.length > 0) {
      return { ...bucket[0], undertone };
    }

    // Last-resort: first spring palette (always present)
    const source = colorPalettes as Record<string, unknown>;
    const spring = Array.isArray(source.spring)
      ? (source.spring as ColorPalette[])
      : [];
    return { ...(spring[0] ?? {}), undertone } as ColorPalette;
  }

  /**
   * Get all available color palettes for an undertone (deterministic order).
   */
  getAllColorPalettes(undertone: UndertoneType): ColorPalette[] {
    const bucket = undertonePalettes[undertone];
    if (!bucket) return [];
    return bucket.map((palette) => ({ ...palette, undertone }));
  }

  /**
   * Highest-level entry point: run seasonal analysis (backend or heuristic)
   * and return the matched palette with prediction metadata attached.
   */
  async getSeasonalPalette(
    undertone: UndertoneType,
    dominantColors: Array<{ hex: string }>
  ): Promise<{ palette: ColorPalette; prediction: SeasonPrediction }> {
    // Derive skin brightness / saturation from dominant colors for the heuristic.
    let skinValue = 0.55;
    let skinSat = 0.30;
    if (dominantColors.length > 0) {
      const hsvSamples = dominantColors
        .map((c) => {
          const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(c.hex);
          if (!m) return null;
          const r = parseInt(m[1], 16) / 255;
          const g = parseInt(m[2], 16) / 255;
          const b = parseInt(m[3], 16) / 255;
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const v = max;
          const s = max === 0 ? 0 : (max - min) / max;
          return { v, s };
        })
        .filter((x): x is { v: number; s: number } => x !== null);
      if (hsvSamples.length > 0) {
        skinValue =
          hsvSamples.reduce((acc, x) => acc + x.v, 0) / hsvSamples.length;
        skinSat =
          hsvSamples.reduce((acc, x) => acc + x.s, 0) / hsvSamples.length;
      }
    }

    // Hierarchical classifier takes precedence when its flag is on and the
    // last analysis produced a result.  The backend call is skipped to avoid
    // two competing systems overwriting each other.
    if (HIERARCHICAL_SEASONAL_ENABLED && this._hierarchicalPrediction) {
      const hier = this._hierarchicalPrediction;
      const prediction: SeasonPrediction = {
        season:            hier.season,
        subtype:           hier.subtype,
        subtypeLabel:      hier.subtypeLabel,
        seasonConfidence:  hier.seasonConfidence,
        subtypeConfidence: hier.subtypeConfidence,
        source:            'hierarchical',
      };
      // Use the predicted season as the undertone bucket so paletteFromPrediction
      // and the palette spread both agree on the same season family.
      const effectiveUndertone = hier.season as UndertoneType;
      const palette = this.getColorPalette(effectiveUndertone, prediction);
      return { palette, prediction };
    }

    let prediction: SeasonPrediction =
      heuristicPrediction(undertone, skinValue, skinSat);

    const backendPrediction = await this.classifySeasonBackend(
      dominantColors,
      legacyUndertoneForApi(undertone)
    );
    if (backendPrediction) {
      prediction = backendPrediction;
    }

    const palette = this.getColorPalette(undertone, prediction);
    return { palette, prediction };
  }

  /**
   * Get makeup recommendations for a specific undertone
   */
  getMakeupRecommendations(
    undertone: UndertoneType
  ): MakeupRecommendations | null {
    const palette = this.getColorPalette(undertone);
    return palette.makeupRecommendations || null;
  }

  /**
   * Advanced skin undertone analysis using multiple techniques.
   * When `faceRegion` is set (e.g. from face detection), only that rectangle is sampled;
   * otherwise the center third of the image is used (legacy behaviour).
   */
  async analyzeSkinUndertoneAdvanced(
    imageData: ImageData,
    faceRegion?: SkinAnalysisRegion | null
  ): Promise<UndertoneType> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      const pixels = imageData.data;
      const width = imageData.width;
      const height = imageData.height;

      let xMin: number;
      let xMax: number;
      let yMin: number;
      let yMax: number;

      if (
        faceRegion &&
        faceRegion.width > 0 &&
        faceRegion.height > 0
      ) {
        const r = faceRegion;
        const x0 = Math.max(0, Math.floor(r.x));
        const y0 = Math.max(0, Math.floor(r.y));
        const x1 = Math.min(width - 1, Math.ceil(r.x + r.width) - 1);
        const y1 = Math.min(height - 1, Math.ceil(r.y + r.height) - 1);
        xMin = x0;
        xMax = Math.max(x0, x1);
        yMin = y0;
        yMax = Math.max(y0, y1);
      } else {
        const centerX = Math.floor(width / 2);
        const centerY = Math.floor(height / 2);
        const regionSize = Math.min(width, height) / 3;
        xMin = Math.max(0, Math.floor(centerX - regionSize));
        xMax = Math.min(width - 1, Math.ceil(centerX + regionSize) - 1);
        yMin = Math.max(0, Math.floor(centerY - regionSize));
        yMax = Math.min(height - 1, Math.ceil(centerY + regionSize) - 1);
      }

      const skinPixels: number[][] = [];
      const allPixels: number[][] = [];

      for (let y = yMin; y <= yMax; y++) {
        for (let x = xMin; x <= xMax; x++) {
          if (x < 0 || x >= width || y < 0 || y >= height) continue;

          const index = (y * width + x) * 4;
          const r = pixels[index];
          const g = pixels[index + 1];
          const b = pixels[index + 2];
          const a = pixels[index + 3];

          if (a < 128) continue;

          const hsv = RGBtoHSV(r, g, b);
          allPixels.push([hsv.h, hsv.s, hsv.v]);

          // More inclusive skin detection
          if (
            hsv.s >= 0.03 && // Even lower minimum
            hsv.s <= 0.95 && // Higher maximum
            hsv.v >= 0.1 && // Lower minimum
            hsv.v <= 0.99 && // Higher maximum
            ((hsv.h >= 0 && hsv.h <= 70) || (hsv.h >= 320 && hsv.h <= 360)) // Even wider hue range
          ) {
            skinPixels.push([hsv.h, hsv.s, hsv.v]);
          }
        }
      }

      if (skinPixels.length === 0) {
        // Fallback to entire image analysis
        return this.analyzeSkinUndertone(imageData);
      }

      // Calculate statistics
      const median = (arr: number[]) => {
        const sorted = [...arr].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 === 0
          ? (sorted[mid - 1] + sorted[mid]) / 2
          : sorted[mid];
      };

      const mean = (arr: number[]) =>
        arr.reduce((a, b) => a + b, 0) / arr.length;

      const hues = skinPixels.map((p) => p[0]);
      const sats = skinPixels.map((p) => p[1]);
      const vals = skinPixels.map((p) => p[2]);

      const medianHue = median(hues);
      const medianSat = median(sats);
      const medianVal = median(vals);
      const meanHue = mean(hues);
      const _meanSat = mean(sats);

      // Multi-criteria classification
      let warmScore = 0;
      let coolScore = 0;
      let neutralScore = 0;

      // Score based on median hue
      if (medianHue >= 15 && medianHue <= 45) warmScore += 2;
      else if (
        (medianHue >= 0 && medianHue < 15) ||
        (medianHue > 45 && medianHue <= 60) ||
        (medianHue >= 330 && medianHue <= 360)
      )
        coolScore += 2;
      else neutralScore += 1;

      // Score based on mean hue
      if (meanHue >= 15 && meanHue <= 45) warmScore += 1;
      else if (
        (meanHue >= 0 && meanHue < 15) ||
        (meanHue > 45 && meanHue <= 60) ||
        (meanHue >= 330 && meanHue <= 360)
      )
        coolScore += 1;
      else neutralScore += 1;

      // Score based on saturation
      if (medianSat < 0.2) neutralScore += 2;
      else if (medianSat >= 0.2 && medianSat <= 0.6) {
        // Higher saturation with warm hues suggests warm undertone
        if (medianHue >= 15 && medianHue <= 45) warmScore += 1;
        // Higher saturation with cool hues suggests cool undertone
        else if (
          (medianHue >= 0 && medianHue < 15) ||
          (medianHue > 45 && medianHue <= 60) ||
          (medianHue >= 330 && medianHue <= 360)
        )
          coolScore += 1;
      }

      // Determine winner
      let hsvUndertone: SeasonUndertone;
      if (warmScore > coolScore && warmScore > neutralScore) {
        // Require both good saturation AND brightness to qualify as spring;
        // deeper/darker warm skin → autumn
        if (medianVal < 0.45) {
          hsvUndertone = 'autumn';
        } else {
          hsvUndertone = medianSat >= 0.38 && medianVal >= 0.55 ? 'spring' : 'autumn';
        }
      } else if (coolScore > warmScore && coolScore > neutralScore) {
        // Deep cool or high saturation cool → winter; lighter cool → summer
        hsvUndertone = medianVal < 0.45 || medianSat >= 0.38 ? 'winter' : 'summer';
      } else {
        hsvUndertone = seasonFromSkin(medianHue, medianSat, medianVal);
      }

      // Hierarchical classifier pass (EXPO_PUBLIC_HIERARCHICAL_SEASONAL=true).
      // When enabled, runs the Lab-based 12-type pipeline on the face ROI and
      // stores the result for getSeasonalPalette to consume.  Falls back to the
      // HSV undertone above if extraction or classification throws.
      if (HIERARCHICAL_SEASONAL_ENABLED && faceRegion) {
        try {
          const features = extractFacialColorFeatures(imageData, faceRegion);
          const hierarchical = classifyHierarchical(features);
          this._hierarchicalPrediction = hierarchical;
          return hierarchical.season as SeasonUndertone;
        } catch {
          this._hierarchicalPrediction = null;
        }
      }

      return hsvUndertone;
    } catch (err) {
      console.error('Error in advanced skin undertone analysis:', err);
      return 'autumn';
    }
  }

  /**
   * Test method to verify undertone analysis with sample colors
   */
  testUndertoneAnalysis(): void {
    // Placeholder for undertone analysis tests
  }

  /**
   * Analyze a color against the detected skin tone
   */
  analyzeColor(colorHex: string): ColorAnalysisResult {
    if (!this.skinToneData) {
      return this.getDefaultAnalysis(colorHex);
    }

    const colorRgb = hexToRgb(colorHex);
    if (!colorRgb) {
      return this.getDefaultAnalysis(colorHex);
    }

    // Calculate various scores
    const undertoneMatch = this.calculateUndertoneMatch(colorHex);
    const contrastScore = this.calculateContrastScore(colorHex);
    const harmonyScore = this.calculateHarmonyScore(colorHex);

    // Combine scores for final result
    const score = this.calculateFinalScore(
      undertoneMatch,
      contrastScore,
      harmonyScore
    );
    const quality = this.getQualityFromScore(score);

    return {
      score,
      quality,
      feedback: this.generateFeedback(quality, colorHex, undertoneMatch),
      tip: this.generateTip(quality, colorHex, this.skinToneData.undertone),
      confidence: this.skinToneData.confidence,
      undertoneMatch,
      contrastScore,
      harmonyScore,
    };
  }

  /**
   * Get color recommendations based on scoring categories
   */
  getColorRecommendations(
    undertone: UndertoneType,
    category: 'best' | 'great' | 'okay' | 'tricky' | 'avoid' = 'best'
  ): string[] {
    const colorScoringData = (
      colorPalettes as {
        colorScoring?: Record<
          string,
          {
            best?: string[];
            great?: string[];
            okay?: string[];
            tricky?: string[];
            avoid?: string[];
          }
        >;
      }
    ).colorScoring?.[legacyUndertoneForApi(undertone)];

    if (!colorScoringData) {
      return [];
    }

    return colorScoringData[category] || [];
  }

  /**
   * Get alternative color suggestions when a color is not optimal
   */
  getAlternativeColors(colorHex: string, undertone: UndertoneType): string[] {
    const colorScoringData = (
      colorPalettes as {
        colorScoring?: Record<
          string,
          {
            best?: string[];
            great?: string[];
            okay?: string[];
            tricky?: string[];
            avoid?: string[];
          }
        >;
      }
    ).colorScoring?.[legacyUndertoneForApi(undertone)];

    if (!colorScoringData) {
      return [];
    }

    // Check which category the current color is in
    let currentCategory: string | null = null;
    for (const [category, colors] of Object.entries(colorScoringData)) {
      if (Array.isArray(colors) && colors.includes(colorHex.toUpperCase())) {
        currentCategory = category;
        break;
      }
    }

    // Return better alternatives based on current category
    if (currentCategory === 'avoid' || currentCategory === 'tricky') {
      return [...colorScoringData.best, ...colorScoringData.great];
    } else if (currentCategory === 'okay') {
      return [...colorScoringData.best, ...colorScoringData.great];
    } else if (currentCategory === 'great') {
      return colorScoringData.best;
    }

    // Default to best colors
    return colorScoringData.best || [];
  }

  /**
   * Save skin analysis results to the backend
   */
  async saveToBackend(
    name: string = 'Skin Analysis',
    imageUri?: string | null
  ): Promise<boolean> {
    if (!this.skinToneData) {
      return false;
    }

    try {
      await saveSkinAnalysis(
        name,
        legacyUndertoneForApi(this.skinToneData.undertone),
        this.skinToneData.skinToneCategory,
        this.skinToneData.confidence,
        this.skinToneData.dominantColors,
        imageUri // Pass the image URI
      );

      return true;
    } catch (error) {
      console.error('❌ Failed to save skin analysis to backend:', error);
      return false;
    }
  }
}

export const skinUndertoneService = new SkinUndertoneService();
